import React from 'react';

function CashDrawerControl({ 
    printerEnabled, 
    cashDrawerEnabled, 
    setCashDrawerEnabled, 
    selectedPrinter,
    cashBalance,
    isLoadingCashBalance,
    isCreatingCashBalance
}) {

    return (
        <div className={`border rounded-lg p-4 ${!printerEnabled ? 'opacity-60 bg-gray-50' : ''}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                    <h4 className="font-medium text-gray-900">💰 Cash Drawer Control</h4>
                    {!printerEnabled && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                            Printer Required
                        </span>
                    )}
                    {/* Toggle Switch */}
                    <div className="flex items-center">
                        <button
                            onClick={() => printerEnabled && setCashDrawerEnabled(!cashDrawerEnabled)}
                            disabled={!printerEnabled || isLoadingCashBalance || isCreatingCashBalance}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                                printerEnabled && !isLoadingCashBalance && !isCreatingCashBalance
                                    ? 'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                                    : 'cursor-not-allowed'
                            } ${
                                cashDrawerEnabled && printerEnabled ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                                    cashDrawerEnabled && printerEnabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                        </button>
                        <span className={`ml-2 text-sm font-medium ${
                            cashDrawerEnabled && printerEnabled 
                                ? 'text-blue-600' 
                                : 'text-gray-500'
                        }`}>
                            {isLoadingCashBalance ? 'Checking...' : 
                             isCreatingCashBalance ? 'Creating...' :
                             (cashDrawerEnabled && printerEnabled ? 'Enabled' : 'Disabled')}
                        </span>
                    </div>
                </div>
                <button 
                    className={`px-3 py-1 rounded text-sm transition-colors ${
                        cashDrawerEnabled && printerEnabled 
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                            : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    }`}
                    disabled={!cashDrawerEnabled || !printerEnabled}
                >
                    Configure
                </button>
            </div>
            <p className="text-sm text-gray-600 mb-3">
                {!printerEnabled 
                    ? 'Enable printer settings first to access cash drawer control.'
                    : 'Manage cash drawer settings and printer connections for this unit.'
                }
            </p>
            
            {/* Cash Drawer Status & Settings */}
            {cashDrawerEnabled && printerEnabled && selectedPrinter && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Connection Status:</span>
                            <span className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                                <span className="text-green-600 font-medium">Ready</span>
                            </span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Printer IP:</span>
                            <span className="text-gray-900 font-mono text-xs">{selectedPrinter.ip}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Auto-open on print:</span>
                            <span className="text-blue-600 font-medium">Yes</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Cash Balance Information */}
            {cashDrawerEnabled && printerEnabled && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200 mt-3">
                    <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-green-800 text-sm flex items-center">
                            💰 Cash Balance
                            {isLoadingCashBalance && (
                                <svg className="animate-spin h-3 w-3 ml-2" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            )}
                        </h5>
                        {cashBalance && (
                            <div className="flex items-center space-x-2">
                                <span className="text-2xl font-bold text-green-600">
                                    ₹{cashBalance.totalAmount || 0}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                    cashBalance.balanceType === 'Opening Balance' 
                                        ? 'bg-green-100 text-green-800' 
                                        : 'bg-blue-100 text-blue-800'
                                }`}>
                                    {cashBalance.balanceType || 'Unknown'}
                                </span>
                            </div>
                        )}
                    </div>
                    
                    {isLoadingCashBalance && (
                        <div className="text-center py-2">
                            <span className="text-sm text-green-600">Searching for existing cash balance...</span>
                        </div>
                    )}
                    
                    {isCreatingCashBalance && (
                        <div className="text-center py-2">
                            <span className="text-sm text-green-600">Creating new cash balance...</span>
                        </div>
                    )}
                    
                    {cashBalance && !isLoadingCashBalance && !isCreatingCashBalance && (
                        <div className="space-y-2">
                            {/* Basic Info */}
                            <div className="space-y-1 text-xs text-green-700">
                                <div className="flex justify-between">
                                    <span>Balance ID:</span>
                                    <span className="font-mono">{cashBalance._id?.slice(-8) || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Balance Date:</span>
                                    <span>{cashBalance.balanceDate ? new Date(cashBalance.balanceDate).toLocaleDateString() : 'N/A'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Last Updated:</span>
                                    <span>{cashBalance.updatedAt ? new Date(cashBalance.updatedAt).toLocaleDateString() : 'N/A'}</span>
                                </div>
                            </div>

                            {/* Denomination Breakdown */}
                            {(cashBalance.note500 > 0 || cashBalance.note200 > 0 || cashBalance.note100 > 0 || 
                              cashBalance.note50 > 0 || cashBalance.note20 > 0 || cashBalance.note10 > 0 ||
                              cashBalance.coin10 > 0 || cashBalance.coin5 > 0 || cashBalance.coin2 > 0 || cashBalance.coin1 > 0) && (
                                <div className="border-t border-green-200 pt-2">
                                    <h6 className="text-xs font-medium text-green-800 mb-1">Cash Breakdown:</h6>
                                    <div className="grid grid-cols-2 gap-1 text-xs text-green-700">
                                        {cashBalance.note500 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹500 Notes:</span>
                                                <span>{cashBalance.note500} (₹{cashBalance.note500 * 500})</span>
                                            </div>
                                        )}
                                        {cashBalance.note200 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹200 Notes:</span>
                                                <span>{cashBalance.note200} (₹{cashBalance.note200 * 200})</span>
                                            </div>
                                        )}
                                        {cashBalance.note100 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹100 Notes:</span>
                                                <span>{cashBalance.note100} (₹{cashBalance.note100 * 100})</span>
                                            </div>
                                        )}
                                        {cashBalance.note50 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹50 Notes:</span>
                                                <span>{cashBalance.note50} (₹{cashBalance.note50 * 50})</span>
                                            </div>
                                        )}
                                        {cashBalance.note20 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹20 Notes:</span>
                                                <span>{cashBalance.note20} (₹{cashBalance.note20 * 20})</span>
                                            </div>
                                        )}
                                        {cashBalance.note10 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹10 Notes:</span>
                                                <span>{cashBalance.note10} (₹{cashBalance.note10 * 10})</span>
                                            </div>
                                        )}
                                        {cashBalance.coin10 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹10 Coins:</span>
                                                <span>{cashBalance.coin10} (₹{cashBalance.coin10 * 10})</span>
                                            </div>
                                        )}
                                        {cashBalance.coin5 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹5 Coins:</span>
                                                <span>{cashBalance.coin5} (₹{cashBalance.coin5 * 5})</span>
                                            </div>
                                        )}
                                        {cashBalance.coin2 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹2 Coins:</span>
                                                <span>{cashBalance.coin2} (₹{cashBalance.coin2 * 2})</span>
                                            </div>
                                        )}
                                        {cashBalance.coin1 > 0 && (
                                            <div className="flex justify-between">
                                                <span>₹1 Coins:</span>
                                                <span>{cashBalance.coin1} (₹{cashBalance.coin1 * 1})</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            {cashBalance.notes && (
                                <div className="border-t border-green-200 pt-2">
                                    <h6 className="text-xs font-medium text-green-800 mb-1">Notes:</h6>
                                    <p className="text-xs text-green-700">{cashBalance.notes}</p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {!cashBalance && !isLoadingCashBalance && !isCreatingCashBalance && (
                        <div className="text-center py-2">
                            <span className="text-sm text-orange-600">⚠️ No cash balance found for this unit</span>
                        </div>
                    )}
                </div>
            )}
            
            {!printerEnabled && (
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <p className="text-sm text-red-600 text-center">
                        <strong>⚠️ Printer must be enabled first</strong><br/>
                        Enable printer settings above to access cash drawer control
                    </p>
                </div>
            )}
            
            {printerEnabled && !cashDrawerEnabled && (
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-sm text-gray-500 text-center">
                        Cash drawer control is disabled for this unit
                    </p>
                </div>
            )}
        </div>
    );
}

export default CashDrawerControl;